import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

import {
  ErrorState,
  KernelLockup,
  Loading,
  Markdown,
  SchemeToggle,
  TypeMap,
  kernel,
  kernelPost,
  resume,
  signIn,
  signOut,
} from "@lopie/kernel-ui";

import { AccessImpact, useAccessImpact } from "./AccessImpact.jsx";
import AppPage from "./AppPage.jsx";
import { ALLOWED_BASIS, AllowedList, LiveLinks, Panel, RUNNING, consoleUrl, money } from "./parts.jsx";
import { awaitingReply, fingerprint, pollDelay } from "./poll.js";
import { countUsers, noteDraft, previousOf } from "./revision.js";
import SandboxPanel from "./SandboxPanel.jsx";
import { followHead, overlayHead } from "./sandbox.js";

/**
 * Builder — build a Kernel app by describing it.
 *
 * The chat is a Managed Agents session on the kernel's `/builder` routes; the
 * panel beside it is drawn from what the session actually did — the stage
 * from the tools it called, the permission summary from the manifest it
 * proposed, the cost from Anthropic's own meter — never from what the agent
 * said about itself.
 */

const STAGE_LABEL = {
  describe: "Describe",
  scaffold: "Scaffold",
  build: "Build",
  approval: "Awaiting approval",
  live: "Approved",
};

// What each build tool means, in words for somebody who has never seen one.
const TOOL_LABEL = {
  design_rules: "Read the design rules",
  schema: "Read the data model",
  remote_contract: "Read the app contract",
  scaffold: "Started the app",
  app_source: "Read the app's code",
  list_templates: "Looked at the apps that can be copied",
  submit_app: "Sent it to build",
  build_status: "Checked the build",
  check_build: "Checked the code",
  run_qa: "Started a QA check",
  qa_status: "Checked QA",
  request_manifest: "Asked for permission",
  manifest_request_status: "Checked the approval",
  cancel_request: "Cancelled the request",
  // The sandbox loop (LO-184): a draft gets a sandbox, the builder reads what a
  // QA run found, fixes it, and sends the draft once a run has passed.
  sandbox_status: "Checked the sandbox",
  run_sandbox_qa: "Started a sandbox QA run",
  sandbox_report: "Read the QA report",
  sandbox_calls: "Read the kernel call log",
  sandbox_egress: "Read the egress log",
  submit_for_approval: "Sent it for approval",
  submit_request: "Sent it for approval",
};

const EXAMPLES = [
  "A page that lists vendors we paid more than $10,000 this quarter, biggest first.",
  "Flag invoices that are 30+ days overdue and let me mark each one as chased.",
];

function SignedOut({ error }) {
  return (
    <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", px: 3 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 440, alignItems: "flex-start" }}>
        <KernelLockup height={26} subtitle="Builder · powered by Lopie One" />
        {error ? (
          <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>
        ) : (
          <Typography color="text.secondary">Signed out.</Typography>
        )}
        <Button variant="contained" onClick={signIn}>Sign in</Button>
      </Stack>
    </Box>
  );
}

/** Not granted: say who can turn it on, and what turning it on does. */
function NoAccess({ refusal }) {
  const recheck = () => {
    // Groups ride in the access token, so a grant made a minute ago is not
    // in this one. Dropping it makes the kit mint a new one from the
    // refresh token, which carries the groups as they are now.
    sessionStorage.removeItem("kernel.token");
    window.location.reload();
  };
  return (
    <Box sx={{ maxWidth: 560, mx: "auto", mt: 8, px: 2 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Builder is off for you</Typography>
          <Typography color="text.secondary">
            {refusal?.detail ||
              "A governor turns Builder on per person on the console's People screen."}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
            <Button variant="contained" onClick={recheck}>Check again</Button>
            <Button href={`${consoleUrl()}/#people`} target="_blank" rel="noreferrer">
              Open People in the console
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

const DOWN_REASONS = [
  ["incorrect", "Incorrect"],
  ["not_what_i_asked", "Not what I asked"],
  ["confusing", "Confusing"],
  ["too_long", "Too long"],
];

/** Rate one reply. Kept per message, so a bad answer can be read next to what
 *  produced it; a governor reads the totals from `GET /builder/feedback`. */
function Rating({ messageId, given, onRate }) {
  const [saved, setSaved] = useState(null);
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const current = saved || given || null;

  const save = async (rating, extra = {}) => {
    setSaving(true);
    setFailed(false);
    try {
      setSaved(await onRate(messageId, { rating, ...extra }));
      setOpen(false);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1} sx={{ mt: 0.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Button size="small" disabled={saving}
          variant={current?.rating === "up" ? "contained" : "outlined"}
          aria-pressed={current?.rating === "up"}
          onClick={() => { setOpen(false); save("up"); }}>
          {current?.rating === "up" ? "Marked helpful" : "Helpful"}
        </Button>
        <Button size="small" disabled={saving}
          variant={current?.rating === "down" ? "contained" : "outlined"}
          aria-pressed={current?.rating === "down"}
          onClick={() => { setReasons(current?.reasons || []); setNote(current?.note || ""); setOpen(true); }}>
          {current?.rating === "down" ? "Marked not helpful" : "Not helpful"}
        </Button>
        {failed ? <Typography variant="caption" color="error">Not saved. Try again.</Typography> : null}
      </Stack>
      {open ? (
        <Stack spacing={1} role="group" aria-label="What was wrong with this reply">
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
            {DOWN_REASONS.map(([key, label]) => (
              <Chip key={key} size="small" label={label} clickable
                color={reasons.includes(key) ? "primary" : "default"}
                variant={reasons.includes(key) ? "filled" : "outlined"}
                aria-pressed={reasons.includes(key)}
                onClick={() => setReasons((r) => (r.includes(key) ? r.filter((x) => x !== key) : [...r, key]))} />
            ))}
          </Stack>
          <TextField size="small" multiline maxRows={4} value={note}
            placeholder="What should it have said or done? (optional)"
            onChange={(e) => setNote(e.target.value)}
            slotProps={{ htmlInput: { "aria-label": "What was wrong", maxLength: 1000 } }} />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={saving}
              onClick={() => save("down", { reasons, note })}>Save</Button>
            <Button size="small" disabled={saving} onClick={() => setOpen(false)}>Cancel</Button>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}

function Bubble({ message, given, onRate }) {
  if (message.role === "user") {
    return (
      <Box sx={{ alignSelf: "flex-end", maxWidth: "85%" }}>
        <Paper sx={{ px: 2, py: 1.25, bgcolor: "action.selected" }} elevation={0}>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {message.text.replace(/^\(I am [^)]*\.\)\s*/, "")}
          </Typography>
        </Paper>
      </Box>
    );
  }
  if (message.role === "agent") {
    return (
      <Box sx={{ maxWidth: "95%" }}>
        <Markdown>{message.text}</Markdown>
        {onRate ? <Rating messageId={message.id} given={given} onRate={onRate} /> : null}
      </Box>
    );
  }
  if (message.role === "tool") {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={`${TOOL_LABEL[message.name] || message.name}${message.times > 1 ? ` ×${message.times}` : ""}`}
        sx={{ alignSelf: "flex-start" }}
      />
    );
  }
  return (
    <Alert severity={message.role === "error" ? "error" : "warning"} sx={{ maxWidth: "95%" }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {message.role === "error"
          ? "The session hit an error"
          : `${TOOL_LABEL[message.name] || message.name || "A step"} was refused`}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {message.text}
      </Typography>
    </Alert>
  );
}

function Progress({ view, onCancel, cancelling, onEditSandbox }) {
  const stages = view?.stages || Object.keys(STAGE_LABEL);
  const active = view ? stages.indexOf(view.stage) : 0;
  return (
    <Panel
      title="Progress"
      basis="From the steps the builder actually ran. Approved is only shown when the kernel says so."
    >
      <Stepper activeStep={active} orientation="vertical">
        {stages.map((s, i) => (
          <Step key={s} completed={i < active || (s === "live" && active === i)}>
            <StepLabel>{STAGE_LABEL[s] || s}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {view?.decision === "rejected" ? (
        <Alert severity="warning" variant="outlined">
          A governor turned this request down
          {view.rejection ? `: ${view.rejection}` : "."} Tell the builder what
          to change and it will ask again.
        </Alert>
      ) : null}
      {view?.decision === "cancelled" ? (
        <Alert severity="info" variant="outlined">
          Request cancelled{view.rejection ? `: ${view.rejection}` : "."} Nothing
          was granted. Ask the builder to fix it and it will send a new one.
        </Alert>
      ) : null}
      {view?.decision === "draft" ? (
        <Stack spacing={1}>
          <Typography variant="body2">
            A draft. Only you can see it, and nobody has been asked. Try it in
            the sandbox below, ask the builder for changes, then submit it for
            approval once the sandbox has passed.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {onEditSandbox ? (
              <Button size="small" variant="outlined" onClick={onEditSandbox}>
                Edit sandbox inputs
              </Button>
            ) : null}
            <Button size="small" color="warning" onClick={onCancel}
              disabled={cancelling}>
              {cancelling ? "Discarding…" : "Discard draft"}
            </Button>
          </Stack>
        </Stack>
      ) : null}
      {view?.stage === "approval" && view?.decision === "pending" ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Button size="small" color="warning" variant="outlined"
            onClick={onCancel} disabled={cancelling}>
            {cancelling ? "Cancelling…" : "Cancel this request"}
          </Button>
        </Stack>
      ) : null}
      {view?.stage === "approval" && (!view?.decision || view.decision === "pending") ? (
        <Typography variant="body2">
          A governor approves it on the{" "}
          <Link href={`${consoleUrl()}/#requests`} target="_blank" rel="noreferrer">
            console's Requests screen
          </Link>
          . The builder cannot approve its own work.
        </Typography>
      ) : null}
      {view?.stage === "live" ? <LiveLinks instances={view.instances} /> : null}
    </Panel>
  );
}

/** What the draft asks to be allowed to do, and (LO-211) how many people in the author's org could not
 *  open it as asked. `now` is the chat's view with the draft that replaced the one it names, which is the
 *  draft the count is asked of; it is read only for a draft, before it is sent. */
function Permissions({ view, now, get }) {
  const lines = view?.proposal_in_words || [];
  const impact = useAccessImpact(get, now?.request_id, now?.decision === "draft" && Boolean(get));
  return (
    <Panel title="What it will be allowed to do">
      <AllowedList lines={lines} basis={ALLOWED_BASIS} />
      <AccessImpact state={impact} />
    </Panel>
  );
}

function Cost({ view }) {
  const cost = view?.cost;
  const cents = cost?.cents || 0;
  const budget = cost?.budget_cents || 1;
  return (
    <Panel title="Cost so far">
      <Typography variant="h5" sx={{ fontVariantNumeric: "tabular-nums" }}>
        ${(cents / 100).toFixed(2)}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, (cents / budget) * 100)}
        aria-label="Share of this build's budget used"
      />
      <Typography variant="caption" color="text.secondary">
        {cost ? `of a $${(budget / 100).toFixed(0)} cap for this build` : "Starts when a build does"}
        {cost?.active_seconds ? ` · ${Math.round(cost.active_seconds / 60)} min working` : ""}
      </Typography>
    </Panel>
  );
}

function DataMap({ get }) {
  const [schema, setSchema] = useState(null);
  const [failure, setFailure] = useState(null);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    get("/schema").then(setSchema).catch(setFailure);
  }, [get]);
  return (
    <Panel
      title="Your data"
      basis="Every type the kernel holds and how they connect. An app can only use what is here."
    >
      {failure ? <FailureView failure={failure} /> : null}
      {!schema && !failure ? <Loading label="Reading the data model" /> : null}
      {schema ? (
        <Box sx={{ minWidth: 0 }}>
          <TypeMap types={schema.types} selected={selected} onSelect={setSelected} minWidth={0} />
        </Box>
      ) : null}
    </Panel>
  );
}

function Chat({ view, busy, onSend, starting, onRate, onStop, stopping }) {
  const [text, setText] = useState("");
  const end = useRef(null);
  // Polling a build is one step, not six: repeats of the same tool collapse.
  const messages = useMemo(() => {
    const out = [];
    for (const m of view?.messages || []) {
      const last = out[out.length - 1];
      if (m.role === "tool" && last?.role === "tool" && last.name === m.name) {
        last.times = (last.times || 1) + 1;
      } else {
        out.push({ ...m });
      }
    }
    return out;
  }, [view]);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    // Cleared once it has been sent, not before: a refused or failed send
    // (a flag, a network error) leaves the words where the person can fix them.
    if (await onSend(value)) setText("");
  };
  const working = busy || starting || awaitingReply(view);

  return (
    <Stack sx={{ minHeight: 0, flex: 1 }} spacing={1.5}>
      <Stack spacing={1.5} sx={{ flex: 1, overflowY: "auto", minHeight: 240, pr: 1 }}>
        {!messages.length && !starting ? (
          <Stack spacing={1.5} sx={{ py: 4 }}>
            <Typography variant="h5">What should the app do?</Typography>
            <Typography color="text.secondary">
              Describe it in plain words. The builder checks what data exists, builds it,
              and asks a governor to approve what it needs before anything goes live.
            </Typography>
            <Stack spacing={1}>
              {EXAMPLES.map((e) => (
                <Button key={e} variant="outlined" onClick={() => setText(e)}
                  sx={{ justifyContent: "flex-start", textAlign: "left", textTransform: "none" }}>
                  {e}
                </Button>
              ))}
            </Stack>
          </Stack>
        ) : null}
        {messages.map((m, i) => (
          <Bubble key={m.id || i} message={m} given={view?.feedback?.[m.id]} onRate={onRate} />
        ))}
        {working ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Working…</Typography>
            {view && RUNNING.includes(view.status) && onStop ? (
              <Button size="small" color="warning" onClick={onStop} disabled={stopping}>
                {stopping ? "Stopping…" : "Stop"}
              </Button>
            ) : null}
          </Stack>
        ) : null}
        <div ref={end} />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-end" }}>
        <TextField
          fullWidth
          multiline
          maxRows={6}
          placeholder={messages.length ? "Reply to the builder" : "Describe the app"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          slotProps={{ htmlInput: { "aria-label": "Message to the builder" } }}
        />
        <Button variant="contained" onClick={send} disabled={busy || !text.trim()}>
          Send
        </Button>
      </Stack>
    </Stack>
  );
}

/** The QA agent's static and governance rules, run on the build before approval. */
function Checks({ get, sessionId, view }) {
  const [checks, setChecks] = useState(null);
  const buildId = view?.build_id;
  const ok = view?.build_ok;
  useEffect(() => {
    setChecks(null);
    if (!ok || !buildId) return;
    let live = true;
    get(`/builder/sessions/${sessionId}/checks`)
      .then((c) => live && setChecks(c))
      .catch(() => live && setChecks({ ready: false, detail: "the checks could not run" }));
    return () => { live = false; };
  }, [get, sessionId, buildId, ok]);
  if (!ok) return null;
  const findings = checks?.findings || [];
  return (
    <Panel title="Checks" basis={checks?.basis}>
      {!checks ? <Loading label="Checking the code" minHeight={60} size={20} /> : null}
      {checks && !checks.ready ? (
        <Typography variant="body2" color="text.secondary">{checks.detail}</Typography>
      ) : null}
      {checks?.ready && !findings.length ? (
        <Typography variant="body2">No issues found by the design and governance rules.</Typography>
      ) : null}
      {findings.length ? (
        <Stack spacing={0.75}>
          <Typography variant="body2">
            {checks.count} {checks.count === 1 ? "issue" : "issues"}. Ask the
            builder to fix them before approval.
          </Typography>
          {findings.slice(0, 8).map((f, i) => (
            <Typography key={i} variant="caption" sx={{ display: "block", wordBreak: "break-word" }}>
              <Chip size="small" variant="outlined" label={f.severity} sx={{ mr: 0.75 }} />
              {f.detail} <code>{f.file}:{f.line}</code>
            </Typography>
          ))}
        </Stack>
      ) : null}
    </Panel>
  );
}

function RequestChip({ request }) {
  if (!request) return <Chip size="small" variant="outlined" label="not requested yet" />;
  const word = { draft: "draft, not sent", pending: "waiting for approval", approved: "approved",
                 rejected: "rejected", superseded: "replaced",
                 cancelled: "cancelled" }[request.state] || request.state;
  return <Chip size="small" variant="outlined" label={word}
    color={request.state === "pending" ? "warning" : request.state === "approved" ? "success" : "default"} />;
}

/** A flagged prompt is a decision, not a network error, so it is not an
 *  ErrorState. It tells the person what happened without alarm and without
 *  repeating the request's words back at them. */
function FailureView({ failure, sx }) {
  if (failure?.error === "flagged_for_review") {
    return (
      <Alert severity="warning" variant="outlined" sx={sx}>
        <AlertTitle>Flagged for review</AlertTitle>
        {failure.message}
      </Alert>
    );
  }
  return <ErrorState failure={failure} sx={sx} />;
}

/** Governors only: prompts the screen stopped before a build started. The
 *  count is the point — it is the same alert that emailed them, in the place
 *  they act. Reviewing one clears it from the list. */
function FlagsPanel({ get, post }) {
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    get("/builder/flags").then(setData).catch(setFailure);
  }, [get]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (flagId) => {
    try {
      await post(`/builder/flags/${flagId}/resolve`, {});
      load();
    } catch (e) { setFailure(e); }
  };
  if (failure) return null; // a non-governor simply does not see this panel
  if (!data || !data.open) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: "warning.main" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {data.open} flagged {data.open === 1 ? "request" : "requests"}
        </Typography>
        <Button size="small" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Review"}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary">{data.basis}</Typography>
      {open ? (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {data.flags.map((f) => (
            <Paper key={f.flag_id} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Chip size="small" color="warning" variant="outlined" label={f.category} />
                <Typography variant="body2" color="text.secondary">
                  {f.tier} tier · {f.username || "unknown"}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={() => resolve(f.flag_id)}>
                  Mark reviewed
                </Button>
              </Stack>
              {f.reason ? <Typography variant="body2" sx={{ mt: 0.5 }}>{f.reason}</Typography> : null}
              <Typography variant="body2" color="text.secondary"
                sx={{ mt: 0.5, fontStyle: "italic", overflowWrap: "anywhere" }}>
                “{f.prompt}”
              </Typography>
            </Paper>
          ))}
        </Stack>
      ) : null}
    </Paper>
  );
}

/** One chat in a list: its title, whether it is working, what it has cost, and
 *  the one thing you can do to the list entry. Archiving takes it off the list;
 *  the chat and everything in it stay, and Restore puts it back. */
function ChatRow({ chat, archived, onOpen, onArchive }) {
  const working = RUNNING.includes(chat.status);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Link component="button" onClick={() => onOpen(chat.id)} sx={{ textAlign: "left" }}>
        {chat.title || chat.id}
      </Link>
      {working ? <Chip size="small" variant="outlined" label="working" /> : null}
      {chat.template ? <Chip size="small" variant="outlined" label={`copy of ${chat.template}`} /> : null}
      {chat.cost_cents ? (
        <Typography variant="caption" color="text.secondary">{money(chat.cost_cents)}</Typography>
      ) : null}
      <Box sx={{ flex: 1 }} />
      <Button size="small" onClick={() => onArchive(chat.id, !archived)}
        aria-label={`${archived ? "Restore" : "Archive"} ${chat.title || chat.id}`}>
        {archived ? "Restore" : "Archive"}
      </Button>
    </Stack>
  );
}

/** What this person has used of the month, with the basis next to the figure. */
function BudgetLine({ budget }) {
  if (!budget) return null;
  const used = budget.cap_cents ? Math.min(100, (budget.spent_cents / budget.cap_cents) * 100) : 0;
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        This month: {money(budget.spent_cents)} of {money(budget.cap_cents)}
        {budget.left_cents <= 0 ? " · nothing new starts until next month or a governor raises it" : ""}
      </Typography>
      <LinearProgress variant="determinate" value={used} aria-label="Share of this month's budget used" />
      <Typography variant="caption" color="text.secondary">
        {budget.source === "person" ? `Your own limit, set by ${budget.set_by || "a governor"}.`
          : budget.source === "default" ? `The limit for everybody, set by ${budget.set_by || "a governor"}.`
          : "The built-in limit: nobody has set one."}{" "}
        {budget.readable === false
          ? "The kernel's limits could not be read just now, so this is the last figure known or the built-in one. "
          : ""}
        A governor changes it on the console's People screen. {budget.basis}
      </Typography>
    </Stack>
  );
}

/** Governors: who is building, what is running now, and what it costs. */
function OverviewPanel({ get }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { get("/builder/overview").then(setData).catch(() => setData(null)); }, [get]);
  if (!data) return null;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Everybody's builds · {data.running.length} working · {money(data.totals.month_cents)} this month
        </Typography>
        <Button size="small" onClick={() => setOpen((v) => !v)}>{open ? "Hide" : "Show"}</Button>
      </Stack>
      {open ? (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {data.running.length ? data.running.map((r) => (
            <Typography key={r.id} variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {r.username} · {r.app || "not filed yet"} · {r.title} · {money(r.cost_cents)} so far
            </Typography>
          )) : <Typography variant="body2" color="text.secondary">Nothing is working right now.</Typography>}
          <Divider />
          {data.people.map((who) => (
            <Typography key={who.username} variant="body2">
              {who.username}: {who.chats} {who.chats === 1 ? "chat" : "chats"} · {money(who.month_cents)} this month
              {who.cap_cents ? ` of ${money(who.cap_cents)}` : ""}
              {who.cap_source === "person" ? " (their own limit)" : who.cap_source === "built_in" ? " (built-in, nobody has set one)" : ""}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary">{data.basis}</Typography>
        </Stack>
      ) : null}
    </Paper>
  );
}

/** The list the server last sent, plus any chat this tab started that it has
 *  not sent yet. A chat is listed from the moment it exists: the server's list
 *  is rebuilt from Anthropic and DynamoDB on every load and can be seconds
 *  behind, and a chat that is building and not in the list reads as lost. */
function withStarted(apps, started) {
  if (!apps) return apps;
  const listed = new Set([...apps.apps.flatMap((a) => a.sessions), ...apps.other_sessions,
    ...(apps.archived_sessions || [])].map((s) => s.id));
  const extra = started.filter((s) => !listed.has(s.id));
  if (!extra.length) return apps;
  const out = {
    ...apps,
    apps: apps.apps.map((a) => ({ ...a, sessions: [...a.sessions] })),
    other_sessions: [...apps.other_sessions],
  };
  for (const s of extra) {
    const home = s.app && out.apps.find((a) => a.app === s.app);
    (home ? home.sessions : out.other_sessions).unshift({ id: s.id, title: s.title });
  }
  return out;
}

/** What a copy says about its source, on the card it is about. The source a
 *  copy starts from is the repository as it was recorded with the release; where
 *  that no longer rebuilds to the release's bytes, the card says which half. */
function sourceNote(t) {
  const drifted = ["function", "frontend"].filter((part) => t.matches?.[part] === false);
  if (!drifted.length) return "";
  return `The ${drifted.join(" and ")} source has moved on from release ${t.version}, `
    + "so a copy will not behave exactly as the running app does.";
}

function TemplateCard({ t, onChoose }) {
  const note = sourceNote(t);
  const blocked = t.fits_pipeline === false;
  return (
    <Paper variant="outlined" sx={{ p: 2, cursor: blocked ? "not-allowed" : "pointer" }}
      component="button" type="button" disabled={blocked}
      onClick={() => onChoose(t.app)} aria-label={`Copy ${t.name || t.app}`}>
      <Stack spacing={0.75} sx={{ textAlign: "left" }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="h6">{t.name || t.app}</Typography>
          <Typography variant="body2" color="text.secondary"
            sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>{t.app}</Typography>
          <Chip size="small" variant="outlined" label={`v${t.version}`} />
          {blocked ? <Chip size="small" color="warning" variant="outlined" label="too large to build here" /> : null}
        </Stack>
        {t.description ? <Typography variant="body2">{t.description}</Typography> : null}
        {t.derived_from ? (
          <Typography variant="caption" color="text.secondary">
            Itself a copy of {t.derived_from.app} {t.derived_from.version}.
          </Typography>
        ) : null}
        {note ? <Typography variant="caption">{note}</Typography> : null}
      </Stack>
    </Paper>
  );
}

/** Every app that has been released, to start a new one from. The list is the
 *  kernel's, so an app is here because it was released and for no other reason. */
function Templates({ templates, failure, onBack, onChoose, onRetry }) {
  const all = templates?.templates || [];
  const shipped = all.filter((t) => t.origin === "shipped");
  const built = all.filter((t) => t.origin !== "shipped");
  return (
    <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
      <Link component="button" onClick={onBack} sx={{ alignSelf: "flex-start" }}>← Your apps</Link>
      <Typography variant="h5">Start from an app</Typography>
      <Alert severity="info" variant="outlined">
        A copy is a new app: its own name, its own releases and its own empty
        data. The app you copy, and anything installed from it, is not changed
        and nobody&apos;s data moves.
      </Alert>
      {failure ? (
        <Stack spacing={1}>
          <FailureView failure={failure} />
          <Box><Button variant="outlined" onClick={onRetry}>Try again</Button></Box>
        </Stack>
      ) : !templates ? <Loading label="Reading the apps that can be copied" /> : null}
      {templates && !all.length ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography>No app has been released yet, so there is nothing to copy.</Typography>
        </Paper>
      ) : null}
      {shipped.length ? (
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">Ships with Kernel</Typography>
          {shipped.map((t) => <TemplateCard key={t.app} t={t} onChoose={onChoose} />)}
        </Stack>
      ) : null}
      {built.length ? (
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">Built here</Typography>
          {built.map((t) => <TemplateCard key={t.app} t={t} onChoose={onChoose} />)}
        </Stack>
      ) : null}
    </Stack>
  );
}

/** The one thing asked of somebody who has chosen an app to copy: what should
 *  be different. What the act does is said beside the button that does it. */
function TemplateStart({ template, loading, failure, name, onBack, onStart, busy }) {
  const [text, setText] = useState("");
  if (!template && loading) return <Loading label="Reading the app" />;
  if (!template) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
        <Link component="button" onClick={onBack} sx={{ alignSelf: "flex-start" }}>← Start from an app</Link>
        {failure ? <FailureView failure={failure} /> : (
          <Alert severity="info" variant="outlined">
            <AlertTitle>{name} is not on the list of apps that can be copied</AlertTitle>
            An app is on that list once it has been released. Go back to see
            what is.
          </Alert>
        )}
      </Stack>
    );
  }
  const note = sourceNote(template);
  return (
    <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
      <Link component="button" onClick={onBack} sx={{ alignSelf: "flex-start" }}>← Start from an app</Link>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="h5">Copy {template.name || template.app}</Typography>
        <Chip size="small" variant="outlined" label={`v${template.version}`} />
      </Stack>
      {template.description ? <Typography variant="body2">{template.description}</Typography> : null}
      {note ? <Alert severity="warning" variant="outlined">{note}</Alert> : null}
      <Panel title="What should be different?">
        <TextField multiline minRows={3} fullWidth value={text}
          placeholder="e.g. add an expiry date to every login, and show the soonest first"
          onChange={(e) => setText(e.target.value)}
          slotProps={{ htmlInput: { "aria-label": `What should be different from ${template.name || template.app}` } }} />
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Button variant="contained" disabled={busy || !text.trim()}
            onClick={() => onStart(template.app, text.trim())}>
            {busy ? "Starting…" : "Start the copy"}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          This starts a new app from a copy of {template.name || template.app} {template.version}.
          It starts with no data of its own, and {template.name || template.app} and
          anything installed from it stay exactly as they are. The copy is built,
          then sent for approval like any other new app. To keep the data an installed
          {" "}{template.name || template.app} already holds, a governor can replace its code with this
          copy from the console&apos;s Apps screen once it is approved.
        </Typography>
      </Panel>
    </Stack>
  );
}

/** Whether an app is running, said in a word and never by colour alone. "Unknown" is
 *  its own answer: when the control plane could not be read the screen must not say
 *  "not live" about an app that may be. */
function runningState(a) {
  if (a.unreadable?.includes("instances")) return { label: "unknown", color: "default" };
  const ready = (a.instances || []).find((i) => i.url && i.status === "ready");
  if (ready) return { label: a.live_version ? `live v${a.live_version}` : "live", color: "success", url: ready.url };
  if (a.instances?.length) {
    const status = a.instances[0].status;
    return { label: !status || status === "pending" ? "installing" : status, color: "warning" };
  }
  return { label: a.live_version ? "approved, not installed" : "not live", color: "default" };
}

/** One row per app: where it stands, what it costs, and what you can do to it. The
 *  table scrolls inside its own box, so a narrow screen never scrolls the page. */
function AppsTable({ apps, onOpen }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
      <Table size="small" aria-label="Your apps"
        sx={{ minWidth: 640, "& .MuiTableCell-root": { px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell>App</TableCell>
            <TableCell>Approval</TableCell>
            <TableCell>Running</TableCell>
            <TableCell align="right">Chats</TableCell>
            <TableCell align="right">Cost</TableCell>
            <TableCell>Last activity</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apps.map((a) => {
            const running = runningState(a);
            const unreadable = a.unreadable?.includes("request") || a.unreadable?.includes("live_version");
            const working = a.sessions.some((c) => RUNNING.includes(c.status));
            return (
              <TableRow key={a.app} hover>
                <TableCell sx={{ minWidth: 190, overflowWrap: "break-word" }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                    <Link component="button" type="button" onClick={() => onOpen(a.app)}
                      sx={{ fontFamily: "monospace", textAlign: "left" }}>
                      {a.app}
                    </Link>
                    {working ? <Chip size="small" variant="outlined" label="working" /> : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  {unreadable
                    ? <Chip size="small" variant="outlined" label="unknown" />
                    : <RequestChip request={a.request} />}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={running.label} color={running.color} />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {a.sessions.length}{a.archived_count ? ` (+${a.archived_count} archived)` : ""}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {money(a.cost_cents)}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}
                  title={a.updated_at ? new Date(a.updated_at * 1000).toLocaleString() : undefined}>
                  {a.updated_at ? new Date(a.updated_at * 1000).toLocaleDateString([], { dateStyle: "medium" }) : "—"}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => onOpen(a.app)}
                      aria-label={`Open ${a.app}`}>
                      Open
                    </Button>
                    {running.url ? (
                      <Button size="small" variant="contained" href={running.url} target="_blank"
                        rel="noreferrer" aria-label={`Open ${a.app} live`}>
                        Open live
                      </Button>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Your apps, by name. The chat is how you change one, not how you find it. */
function AppsHome({ apps, failure, refreshing, onRetry, onOpen, onNew, onTemplates, onOpenSession, onArchive, flagsPanel }) {
  if (!apps && failure) {
    return (
      <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
        <FailureView failure={failure} />
        <Box><Button variant="outlined" onClick={onRetry}>Try again</Button></Box>
      </Stack>
    );
  }
  if (!apps) return <Loading label="Reading your apps" />;
  return (
    <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto", p: 2 }}>
      {flagsPanel}
      <BudgetLine budget={apps.budget} />
      {apps.degraded ? (
        <Alert severity="warning" variant="outlined"
          action={<Button size="small" color="inherit" onClick={onRetry}>Try again</Button>}>
          <AlertTitle>Your apps could not be read</AlertTitle>
          Your chats are listed below and open as usual. They are not grouped by
          app until this loads.
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Your apps</Typography>
        {refreshing ? <Typography variant="caption" color="text.secondary">Refreshing…</Typography> : null}
        <Button variant="outlined" onClick={onTemplates}>Start from an app</Button>
        <Button variant="contained" onClick={onNew}>New app</Button>
      </Stack>
      {!apps.apps.length && !apps.degraded ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography>No apps yet. Describe one and the builder makes it.</Typography>
        </Paper>
      ) : null}
      {apps.apps.length ? <AppsTable apps={apps.apps} onOpen={onOpen} /> : null}
      {apps.other_sessions?.length ? (
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            {apps.degraded ? "Your chats" : "Chats not tied to an app yet"}
          </Typography>
          {apps.other_sessions.map((s) => (
            <ChatRow key={s.id} chat={s} onOpen={onOpenSession} onArchive={onArchive} />
          ))}
        </Stack>
      ) : null}
      {apps.archived_sessions?.length ? (
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            Archived chats ({apps.archived_sessions.length})
          </Typography>
          {apps.archived_sessions.map((s) => (
            <ChatRow key={s.id} chat={s} archived onOpen={onOpenSession} onArchive={onArchive} />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

/** The browser's refresh token, handed to Builder so a build keeps its
 *  sign-in after this tab closes. Same key the kit keeps it under. */
function withRefresh(body) {
  let refresh = "";
  try {
    refresh = localStorage.getItem(`kernel.refresh.${window.KERNEL_CONFIG?.clientId}`) || "";
  } catch {
    refresh = "";
  }
  return refresh ? { ...body, refresh_token: refresh } : body;
}

/** `#`-routes: "" apps · "new" · "templates" · "template=<app>" · "app=<name>" · "sesn_…".
 *  Anything else is the apps list, so a stale link lands on a page that works. */
const ROUTES = /^(new|templates|template=.+|app=.+|sesn_.+)$/;
const rawHash = () => window.location.hash.replace("#", "");
function readRoute() {
  const hash = rawHash();
  return ROUTES.test(hash) ? hash : "";
}

export default function App() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);
  const [apps, setApps] = useState(null);
  const [route, setRoute] = useState(readRoute);
  const current = route.startsWith("sesn_") ? route : "";
  const appName = route.startsWith("app=") ? route.slice(4) : "";
  const templateName = route.startsWith("template=") ? route.slice(9) : "";
  const choosing = route === "templates" || !!templateName;
  const [templates, setTemplates] = useState(null);
  const [templatesFailure, setTemplatesFailure] = useState(null);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [appsFailure, setAppsFailure] = useState(null);
  const [appsLoading, setAppsLoading] = useState(false);
  // Chats this tab has started, so each is on the list from the moment it
  // exists rather than from the next load that happens to include it.
  const [started, setStarted] = useState([]);

  useEffect(() => {
    const deliberate = new URLSearchParams(window.location.search).has("signedout");
    resume()
      .then((found) => {
        setSession(found);
        if (!found && !deliberate) {
          signIn();
          return;
        }
        setReady(true);
      })
      .catch((e) => {
        setError(e.message);
        setReady(true);
      });
  }, []);

  useEffect(() => {
    const follow = () => setRoute(readRoute());
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, []);
  const go = useCallback((next) => {
    if (rawHash() !== next) window.location.hash = next;
    setRoute(next);
  }, []);

  const get = useMemo(() => (session ? kernel(session.token) : null), [session]);
  const post = useMemo(() => (session ? kernelPost(session.token) : null), [session]);

  // The apps that can be copied, read when somebody looks at them and not
  // before: it is the kernel's list, drawn as it came.
  const loadTemplates = useCallback(() => {
    if (!get) return;
    setTemplatesFailure(null);
    get("/builder/templates").then(setTemplates).catch(setTemplatesFailure);
  }, [get]);
  useEffect(() => {
    if (choosing && !templates && !templatesFailure) loadTemplates();
  }, [choosing, templates, templatesFailure, loadTemplates]);

  // Only the newest load may write. Two are routinely in flight (the first
  // paint asks twice) and the slower one is the older, so without this a
  // response from before a chat was started lands afterwards and removes it.
  const appsSeq = useRef(0);
  const appsAt = useRef(0);
  const loadApps = useCallback((force = false) => {
    if (!get) return;
    // The first paint asks twice (once for who you are, once for the route). The
    // list takes a second or more to build, so the second ask is the same list.
    // Anything that has just changed it passes `force`.
    if (!force && Date.now() - appsAt.current < 500) return;
    appsAt.current = Date.now();
    const mine = ++appsSeq.current;
    const current = () => mine === appsSeq.current;
    setAppsLoading(true);
    get("/builder/apps")
      .then((answer) => { if (current()) { setApps(answer); setAppsFailure(null); } })
      .catch(async (e) => {
        if (!current()) return;
        setAppsFailure(e);
        // Grouping them failed. Getting back into a chat does not need it.
        try {
          const flat = await get("/builder/sessions");
          if (current()) setApps({ apps: [], other_sessions: flat.sessions || [], degraded: true });
        } catch { /* the failure above is what is shown */ }
      })
      .finally(() => { if (current()) setAppsLoading(false); });
  }, [get]);

  useEffect(() => {
    if (!get) return;
    get("/builder/me")
      .then((answer) => {
        setMe(answer);
        if (answer.admitted) loadApps();
      })
      .catch(setFailure);
  }, [get, loadApps]);

  // Which build is on screen *now*. A poll for the previous one can still be
  // in flight when the route changes, and without this check its answer
  // lands afterwards and puts the old history back.
  const shown = useRef(current);
  // Looks at the open chat now and starts counting quiet looks again. Set by the
  // polling effect below; what the person does (send, stop) calls it so that
  // the answer is not left waiting out a long gap.
  const nudge = useRef(null);

  const refresh = useCallback(async () => {
    if (!get || !current) return null;
    try {
      const next = await get(`/builder/sessions/${current}`);
      if (shown.current !== current) return null;
      setView(next);
      setFailure(null);
      return next;
    } catch (e) {
      if (shown.current === current) setFailure(e);
      return null;
    }
  }, [get, current]);

  useEffect(() => {
    shown.current = current;
    setFailure(null);
    setView(null);
    if (!current) return undefined;
    let stop = false;
    let timer;
    let unchanged = 0;
    let last = "";
    const schedule = (view) => {
      clearTimeout(timer);
      const delay = pollDelay({ working: awaitingReply(view), hidden: document.hidden, unchanged });
      if (delay !== null) timer = setTimeout(loop, delay);
    };
    const loop = async () => {
      const next = await refresh();
      if (stop) return;
      const seen = fingerprint(next);
      unchanged = next && seen === last ? unchanged + 1 : 0;
      last = seen;
      schedule(next);
    };
    const wake = () => {
      if (stop) return;
      unchanged = 0;
      clearTimeout(timer);
      loop();
    };
    // A tab nobody can see is not looked at, and is looked at the moment it is.
    const onVisibility = () => (document.hidden ? clearTimeout(timer) : wake());
    document.addEventListener("visibilitychange", onVisibility);
    nudge.current = wake;
    loop();
    return () => {
      stop = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      if (nudge.current === wake) nudge.current = null;
    };
  }, [current, refresh]);

  useEffect(() => {
    if (route === "" || appName) loadApps();
  }, [route, appName, loadApps]);

  // The request the chat names, when it has since been replaced. Editing a
  // draft's sandbox inputs makes a new draft and closes the old one, and the
  // chat still holds the id its agent last proposed: without this the draft
  // panel, and Send for approval with it, would go the moment somebody saved.
  const [head, setHead] = useState(null);
  const replaced = view?.decision === "superseded" ? view.request_id : "";
  useEffect(() => {
    if (!get || !replaced) return undefined;
    let live = true;
    followHead((id) => get(`/manifest-requests/${id}`), replaced)
      .then((found) => live && setHead({ from: replaced, request: found }))
      .catch(() => { /* the chat shows what it knew */ });
    return () => { live = false; };
  }, [get, replaced]);
  const now = overlayHead(view, head);

  // The sandbox panel says when the chat has moved past its draft (LO-206). Two
  // things it cannot read off the chat's view are kept here: a message this tab
  // has just sent, which the view does not hold for a few seconds (so Submit is
  // held at once and not a poll later), and the drafts this tab has shown, so the
  // panel for a new draft can say which one it replaced. Both belong to one chat,
  // and `asked` to one draft: a new draft is the answer to it.
  const [asked, setAsked] = useState(null);
  const [trail, setTrail] = useState([]);
  useEffect(() => {
    setAsked(null);
    setTrail([]);
  }, [current]);
  useEffect(() => {
    setAsked(null);
  }, [now?.request_id]);
  const rememberDraft = useCallback((fact) => setTrail((was) => noteDraft(was, fact)), []);

  const [cancelling, setCancelling] = useState(false);
  // Sends the draft to the governors' queue. The kernel's refusal is thrown to
  // the sandbox panel, which shows it as the kernel said it beside the button
  // that asked; it is not a chat failure. Then tells the builder, so its
  // picture of the build matches what the kernel now holds. By then the draft
  // has gone, so that failing is a chat failure and not a refusal.
  const onSubmitDraft = async (requestId) => {
    await post(`/manifest-requests/${requestId}/submit`, {});
    try {
      await post(`/builder/sessions/${current}/messages`, withRefresh({
        message: `I sent draft ${requestId} for approval from the screen. ` +
          "It is pending now; you do not need to submit it.",
      }));
      nudge.current?.();
    } catch (e) {
      setFailure(e);
    }
  };
  // Straight to the kernel with this person's Builder token: the request
  // came through Builder, so Builder may withdraw it. Then the agent is told,
  // so its picture of the build is not a request that no longer exists.
  const onCancel = async () => {
    if (!now?.request_id) return;
    const isDraft = now.decision === "draft";
    // A draft was never shown to a governor, so there is nobody to explain it to.
    const reason = isDraft
      ? (window.confirm("Discard this draft? The chat keeps going.") ? "" : null)
      : window.prompt("Why cancel? (optional, shown to the governor)") ?? null;
    if (reason === null) return;
    setCancelling(true);
    try {
      await post(`/manifest-requests/${now.request_id}/cancel`, { reason });
      await post(`/builder/sessions/${current}/messages`, withRefresh({
        message: isDraft
          ? `I discarded draft ${now.request_id}. Keep going; save a new draft after the next passing build.`
          : `I cancelled permission request ${now.request_id}` +
            (reason ? ` because: ${reason}` : ".") +
            " Don't send a new one until I ask.",
      }));
      nudge.current?.();
    } catch (e) {
      setFailure(e);
    } finally {
      setCancelling(false);
    }
  };

  const [stopping, setStopping] = useState(false);
  // Halts what the builder is doing. It goes idle and can be spoken to again.
  const onStop = async () => {
    setStopping(true);
    try {
      await post(`/builder/sessions/${current}/stop`, {});
      setTimeout(() => nudge.current?.(), 1500);
    } catch (e) {
      setFailure(e);
    } finally {
      setStopping(false);
    }
  };
  // Off the list or back on it; the chat itself is not touched.
  const onArchive = async (id, archive) => {
    try {
      await post(`/builder/sessions/${id}/${archive ? "archive" : "unarchive"}`, {});
      setStarted((all) => all.filter((x) => x.id !== id));
      loadApps(true);
      if (id === current) go(chatApp ? `app=${chatApp}` : "");
    } catch (e) {
      setFailure(e);
    }
  };

  const onRate = current
    ? (messageId, rating) => post(`/builder/sessions/${current}/feedback`, { message_id: messageId, ...rating })
    : null;

  const onSend = async (text) => {
    setBusy(true);
    try {
      if (!current) {
        const made = await post("/builder/sessions", withRefresh({ message: text }));
        setStarted((all) => [{ id: made.session_id, title: text.slice(0, 80), app: "" }, ...all]);
        go(made.session_id);
      } else {
        // Before the post, so the draft's panel holds Submit as the person presses
        // Send. A send the kernel refuses puts it back, in the catch below.
        setAsked({ at: Date.now(), users: countUsers(view?.messages), requestId: now?.request_id || "" });
        await post(`/builder/sessions/${current}/messages`, withRefresh({ message: text }));
        setTimeout(() => nudge.current?.(), 1200);
      }
      setFailure(null);
      return true;
    } catch (e) {
      setAsked(null);
      setFailure(e);
    } finally {
      setBusy(false);
    }
  };

  const onStartFromTemplate = async (source, text) => {
    setBusy(true);
    try {
      const made = await post(`/builder/templates/${source}/sessions`, withRefresh({ message: text }));
      setStarted((all) => [{ id: made.session_id, title: `From ${source}: ${text}`.slice(0, 80), app: "" }, ...all]);
      setFailure(null);
      go(made.session_id);
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  };

  const onChange = async (text) => {
    setBusy(true);
    try {
      const made = await post(`/builder/apps/${appName}/sessions`, withRefresh({ message: text }));
      setStarted((all) => [{ id: made.session_id, title: text.slice(0, 80), app: appName }, ...all]);
      go(made.session_id);
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return <Loading label="Restoring your session" minHeight="100vh" />;
  if (!session) return <SignedOut error={error} />;

  const shownApps = withStarted(apps, started);
  const thisApp = shownApps?.apps.find((a) => a.app === appName) || null;
  const chatApp = view?.app || "";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Box sx={{ height: 2, bgcolor: "primary.main" }} />
      <AppBar position="sticky" color="default" elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Toolbar sx={{ gap: 1.5, flexWrap: "wrap", py: 1 }}>
          <KernelLockup height={22} subtitle="powered by Lopie One" />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Link component="button" onClick={() => go("")} underline="hover"
            sx={{ typography: "subtitle1", fontWeight: 600, color: "text.primary" }}>
            Builder
          </Link>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Chip size="small" variant="outlined"
            label={session.claims.username || session.claims.sub} sx={{ maxWidth: 220 }} />
          <SchemeToggle />
          <Button size="small" onClick={signOut}>Sign out</Button>
        </Toolbar>
      </AppBar>

      {/* The chat grid below has its own FailureView once it is on screen
          (current, or a brand-new session with route === "new"); this one is
          only for a failure with no chat panel to carry it — the apps list,
          /me. The two conditions must stay exact opposites, or
          a flag on the first message of a new app renders the alert twice. */}
      {failure && !current && route !== "new" ? (
        <Box sx={{ maxWidth: 960, mx: "auto", px: 2, pt: 2 }}><FailureView failure={failure} /></Box>
      ) : null}

      {!me && !failure ? <Loading label="Checking your access" /> : null}
      {me && !me.admitted ? <NoAccess refusal={me.refusal} /> : null}

      {me?.admitted && route === "" ? (
        <AppsHome apps={shownApps} failure={appsFailure} refreshing={appsLoading}
          onRetry={() => loadApps(true)} onOpen={(a) => go(`app=${a}`)} onNew={() => go("new")}
          onTemplates={() => go("templates")}
          onOpenSession={go} onArchive={onArchive}
          flagsPanel={me?.governor ? (
            <><FlagsPanel get={get} post={post} /><OverviewPanel get={get} /></>
          ) : null} />
      ) : null}

      {me?.admitted && route === "templates" ? (
        <Templates templates={templates} failure={templatesFailure}
          onBack={() => go("")} onChoose={(a) => go(`template=${a}`)}
          onRetry={() => { setTemplates(null); loadTemplates(); }} />
      ) : null}

      {me?.admitted && templateName ? (
        <TemplateStart template={templates?.templates?.find((t) => t.app === templateName) || null}
          loading={!templates && !templatesFailure} failure={templatesFailure} name={templateName}
          onBack={() => go("templates")} onStart={onStartFromTemplate} busy={busy} />
      ) : null}

      {me?.admitted && appName ? (
        <AppPage app={thisApp} loading={appsLoading || !apps} degraded={!!apps?.degraded} name={appName}
          onBack={() => go("")} onOpenSession={go} onArchive={onArchive}
          onChange={onChange} busy={busy}
          onReplaced={() => loadApps(true)}
          get={get} post={post} />
      ) : null}

      {me?.admitted && (current || route === "new") ? (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 360px" },
          gap: 2,
          p: 2,
          maxWidth: 1280,
          mx: "auto",
        }}>
          <Paper variant="outlined" sx={{
            p: 2, display: "flex", flexDirection: "column",
            height: { md: "calc(100vh - 110px)" }, minHeight: 480, minWidth: 0,
          }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              <Link component="button" onClick={() => go(chatApp ? `app=${chatApp}` : "")}>
                ← {chatApp || "Your apps"}
              </Link>
              <Box sx={{ flex: 1 }} />
              {current ? (
                <Button size="small" onClick={() => onArchive(current, true)}>Archive chat</Button>
              ) : null}
              {current ? (
                <Button size="small" onClick={() => go("new")}>New app</Button>
              ) : null}
            </Stack>
            {failure ? <FailureView failure={failure} sx={{ mb: 1.5 }} /> : null}
            <Chat view={view} busy={busy} onSend={onSend} starting={(busy && !current) || (!!current && !view && !failure)} onRate={onRate}
              onStop={current ? onStop : null} stopping={stopping} />
          </Paper>
          {/* Same height as the chat on a wide screen, scrolling on its own,
              so the panels never run on below the conversation. */}
          <Stack spacing={2} sx={{
            minWidth: 0,
            height: { md: "calc(100vh - 110px)" },
            overflowY: { md: "auto" },
            pr: { md: 0.5 },
          }}>
            <Progress view={now} onCancel={onCancel} cancelling={cancelling}
              onEditSandbox={chatApp ? () => go(`app=${chatApp}`) : null} />
            {now?.request_id && (now.decision === "draft" || now.decision === "pending") ? (
              // Keyed by the request, so a draft that is replaced starts the
              // panel again for the one that replaced it.
              <SandboxPanel key={now.request_id} get={get} post={post} requestId={now.request_id}
                onSubmit={onSubmitDraft}
                chat={now} asked={asked && asked.requestId === now.request_id ? asked : null}
                previous={previousOf(trail, now.request_id)} onDraft={rememberDraft} />
            ) : null}
            {current ? <Checks get={get} sessionId={current} view={view} /> : null}
            <Permissions view={view} now={now} get={get} />
            <Cost view={view} />
            <DataMap get={get} />
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
